import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useGetTrainerAvailability, useGetTrainer, useListLocations, useListLocationAvailability, useGetTrainerLocations } from "@workspace/api-client-react";
import { apiFetch } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Clock, Plus, Trash2, Save, CalendarOff, Building2, Info, Copy, MapPin } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

type Slot = { id?: string; dayOfWeek: number; startTime: string; endTime: string; isRecurring: boolean };
type BlockedDate = { id: string; trainerId: string; date: string; reason: string | null };

function formatTimeDisplay(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const period = h < 12 ? "AM" : "PM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${m.toString().padStart(2, "0")} ${period}`;
}


type GymHoursInfo = {
  mode: "CUSTOM";
  blocksByDay: Record<number, { startTime: string; endTime: string }[]>;
  locationName: string;
  locationId: string | null;
};

function useLocationHours(locationId: string | null | undefined, gymId: string | null | undefined): GymHoursInfo {
  const { data: locations = [] } = useListLocations(gymId ?? "", {
    query: { enabled: !!gymId },
  });

  const loc = useMemo(() => {
    if (!locationId || !locations.length) return undefined;
    return locations.find(l => l.id === locationId && l.isActive);
  }, [locations, locationId]);

  const { data: blocks = [] } = useListLocationAvailability(loc?.id ?? "", {
    query: {
      enabled: !!loc && loc.availabilityMode === "CUSTOM",
    },
  });

  const blocksByDay = useMemo(() => {
    const map: Record<number, { startTime: string; endTime: string }[]> = {};
    for (let i = 0; i < 7; i++) map[i] = [];
    blocks.filter(b => b.isActive).forEach(b => {
      map[b.dayOfWeek].push({ startTime: b.startTime, endTime: b.endTime });
    });
    return map;
  }, [blocks]);

  if (!loc) return { mode: "CUSTOM", blocksByDay: {}, locationName: "", locationId: null };

  return {
    mode: "CUSTOM" as const,
    blocksByDay,
    locationName: loc.name,
    locationId: loc.id,
  };
}


export default function TrainerAvailability() {
  const { user } = useAuth();
  const { toast } = useToast();

  const { data: trainerProfile } = useGetTrainer(user?.id || "", { query: { enabled: !!user?.id } });
  const { data: locations = [] } = useListLocations(user?.gymId || "", { query: { enabled: !!user?.gymId } });
  const { data: trainerLocationIds = [] } = useGetTrainerLocations(user?.id || "", { query: { enabled: !!user?.id } });

  const assignedLocations = useMemo(() => {
    if (!locations.length) return [];
    if (trainerLocationIds.length > 0) {
      return locations.filter(l => trainerLocationIds.includes(l.id) && l.isActive);
    }
    if (trainerProfile?.primaryLocationId) {
      const loc = locations.find(l => l.id === trainerProfile.primaryLocationId && l.isActive);
      if (loc) return [loc];
    }
    const primary = locations.find(l => l.isPrimary && l.isActive);
    if (primary) return [primary];
    const first = locations.find(l => l.isActive);
    return first ? [first] : [];
  }, [locations, trainerLocationIds, trainerProfile?.primaryLocationId]);

  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [sameForAll, setSameForAll] = useState(false);

  useEffect(() => {
    if (assignedLocations.length > 0 && !selectedLocationId) {
      setSelectedLocationId(assignedLocations[0].id);
    }
  }, [assignedLocations, selectedLocationId]);

  const activeLocationId = selectedLocationId || assignedLocations[0]?.id || null;
  const gymHours = useLocationHours(activeLocationId, user?.gymId);

  const { data: existing, refetch } = useGetTrainerAvailability(user?.id || "", { locationId: activeLocationId || undefined }, { query: { enabled: !!user?.id && !!activeLocationId } });

  const [slots, setSlots] = useState<Slot[]>([]);
  const [saving, setSaving] = useState(false);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [newBlockDate, setNewBlockDate] = useState("");
  const [newBlockReason, setNewBlockReason] = useState("");

  useEffect(() => {
    if (user?.id) {
      apiFetch(`/availability/blocked-dates/${user.id}`).then(setBlockedDates).catch(() => {});
    }
  }, [user?.id]);

  async function addBlockedDate() {
    if (!newBlockDate || !user?.id) return;
    try {
      const result = await apiFetch("/availability/blocked-dates", {
        method: "POST",
        body: JSON.stringify({ trainerId: user.id, date: newBlockDate, reason: newBlockReason || null }),
      });
      setBlockedDates([...blockedDates, result as BlockedDate]);
      setNewBlockDate("");
      setNewBlockReason("");
      toast({ title: "Date blocked" });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  }

  async function removeBlockedDate(id: string) {
    try {
      await apiFetch(`/availability/blocked-dates/${id}`, { method: "DELETE" });
      setBlockedDates(blockedDates.filter(d => d.id !== id));
      toast({ title: "Blocked date removed" });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  }

  useEffect(() => {
    if (existing && Array.isArray(existing)) {
      setSlots(existing.map((s: any) => ({ id: s.id, dayOfWeek: s.dayOfWeek, startTime: s.startTime, endTime: s.endTime, isRecurring: s.isRecurring ?? true })));
    }
  }, [existing]);

  function addSlot(day: number) {
    let defaultStart = "09:00";
    let defaultEnd = "17:00";
    if (gymHours.mode === "CUSTOM") {
      const dayBlocks = gymHours.blocksByDay[day];
      if (dayBlocks && dayBlocks.length > 0) {
        defaultStart = dayBlocks[0].startTime;
        defaultEnd = dayBlocks[0].endTime;
      }
    }
    setSlots([...slots, { dayOfWeek: day, startTime: defaultStart, endTime: defaultEnd, isRecurring: true }]);
  }

  function removeSlot(index: number) {
    const slot = slots[index];
    if (slot.id) {
      apiFetch(`/availability/${slot.id}`, { method: "DELETE" }).then(() => {
        refetch();
        toast({ title: "Slot removed" });
      });
    }
    setSlots(slots.filter((_, i) => i !== index));
  }

  function updateSlot(index: number, field: keyof Slot, value: any) {
    const updated = [...slots];
    (updated[index] as any)[field] = value;
    setSlots(updated);
  }


  async function saveAll() {
    setSaving(true);
    try {
      if (sameForAll && assignedLocations.length > 1) {
        const slotData = slots.map(s => ({ dayOfWeek: s.dayOfWeek, startTime: s.startTime, endTime: s.endTime }));
        await apiFetch("/availability/bulk", {
          method: "POST",
          body: JSON.stringify({
            trainerId: user?.id,
            locationIds: assignedLocations.map(l => l.id),
            slots: slotData,
          }),
        });
        toast({ title: "Availability saved for all locations" });
      } else {
        for (const slot of slots) {
          if (slot.id) {
            await apiFetch(`/availability/${slot.id}`, { method: "PUT", body: JSON.stringify({ ...slot, locationId: activeLocationId }) });
          } else {
            await apiFetch("/availability", { method: "POST", body: JSON.stringify({ ...slot, trainerId: user?.id, gymId: user?.gymId, locationId: activeLocationId }) });
          }
        }
        toast({ title: "Availability saved" });
      }
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  const hasMultipleLocations = assignedLocations.length > 1;

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Availability</h1>
            <p className="text-muted-foreground">Set your weekly availability for bookings</p>
          </div>
          <Button onClick={saveAll} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/80">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save All"}
          </Button>
        </div>

        {assignedLocations.length > 1 && (
          <Card className="glass-panel border-white/5">
            <CardContent className="p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-primary shrink-0" />
                  <span className="text-sm font-semibold text-white">Location</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {assignedLocations.map(loc => (
                    <button
                      key={loc.id}
                      onClick={() => { setSelectedLocationId(loc.id); setSameForAll(false); }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeLocationId === loc.id
                          ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                          : "bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      {loc.name}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3 pt-2 border-t border-white/5">
                  <Switch
                    checked={sameForAll}
                    onCheckedChange={setSameForAll}
                    id="same-for-all"
                  />
                  <label htmlFor="same-for-all" className="text-sm text-muted-foreground cursor-pointer">
                    Same schedule for all locations
                  </label>
                  {sameForAll && (
                    <span className="text-xs text-primary font-medium ml-auto">
                      Saving will copy this schedule to all {assignedLocations.length} locations
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {gymHours.mode === "CUSTOM" && gymHours.locationName && (
          <Card className="glass-panel border-blue-500/20 bg-blue-500/5">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-white mb-1">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    {gymHours.locationName} Operating Hours
                  </p>
                  <p className="text-xs text-muted-foreground mb-2">
                    Your gym's current operating hours are shown below for reference. Slots outside these hours can still be saved but won't be bookable until hours overlap.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1">
                    {DAYS.map((day, i) => {
                      const blocks = gymHours.blocksByDay[i] || [];
                      return (
                        <div key={i} className="text-xs">
                          <span className="text-white font-medium">{day.slice(0, 3)}: </span>
                          {blocks.length === 0 ? (
                            <span className="text-red-400">Closed</span>
                          ) : (
                            <span className="text-muted-foreground">
                              {blocks.map(b => `${formatTimeDisplay(b.startTime)} – ${formatTimeDisplay(b.endTime)}`).join(", ")}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}


        <div className="space-y-6">
          {DAYS.map((day, dayIndex) => {
            const daySlots = slots.filter((s) => s.dayOfWeek === dayIndex);
            const gymDayBlocks = gymHours.blocksByDay[dayIndex] || [];
            const gymClosed = gymHours.mode === "CUSTOM" && gymDayBlocks.length === 0;
            const prevDayIndex = dayIndex - 1;
            const previousDaySlots = prevDayIndex >= 0 ? slots.filter(s => s.dayOfWeek === prevDayIndex) : [];
            const hasPreviousDaySlots = previousDaySlots.length > 0;
            const isSameAsPrevious = hasPreviousDaySlots &&
              daySlots.length === previousDaySlots.length &&
              previousDaySlots.every(ps =>
                daySlots.some(ds => ds.startTime === ps.startTime && ds.endTime === ps.endTime)
              );

            const handleCopyPrevious = async (checked: boolean) => {
              const existingDaySlots = slots.filter(s => s.dayOfWeek === dayIndex);
              const slotsWithIds = existingDaySlots.filter(s => s.id);
              for (const slot of slotsWithIds) {
                try {
                  await apiFetch(`/availability/${slot.id}`, { method: "DELETE" });
                } catch {}
              }
              if (checked && hasPreviousDaySlots) {
                const newSlots = slots.filter(s => s.dayOfWeek !== dayIndex);
                const copiedSlots = previousDaySlots.map(ps => ({
                  dayOfWeek: dayIndex,
                  startTime: ps.startTime,
                  endTime: ps.endTime,
                  isRecurring: ps.isRecurring,
                }));
                setSlots([...newSlots, ...copiedSlots]);
              } else {
                setSlots(slots.filter(s => s.dayOfWeek !== dayIndex));
              }
            };

            return (
              <Card key={day} className="glass-panel border-white/5">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CardTitle className="text-lg font-display text-white">{day}</CardTitle>
                      {gymHours.mode === "CUSTOM" && (
                        gymClosed ? (
                          <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">Gym Currently Closed</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {gymDayBlocks.map(b => `${formatTimeDisplay(b.startTime)} – ${formatTimeDisplay(b.endTime)}`).join(", ")}
                          </span>
                        )
                      )}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => addSlot(dayIndex)} className="text-primary hover:bg-primary/10">
                      <Plus className="w-4 h-4 mr-1" /> Add Slot
                    </Button>
                  </div>
                  {prevDayIndex >= 0 && hasPreviousDaySlots && !gymClosed && (
                    <div className="flex items-center gap-2 mt-2">
                      <Checkbox
                        id={`copy-prev-trainer-${dayIndex}`}
                        checked={isSameAsPrevious}
                        onCheckedChange={(checked) => handleCopyPrevious(!!checked)}
                      />
                      <label
                        htmlFor={`copy-prev-trainer-${dayIndex}`}
                        className="text-xs text-muted-foreground cursor-pointer select-none flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        Same as {DAYS[prevDayIndex]}
                      </label>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {daySlots.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No availability set</p>
                  ) : (
                    <div className="space-y-3">
                      {daySlots.map((slot) => {
                        const globalIdx = slots.indexOf(slot);
                        return (
                          <div key={globalIdx}>
                            <div className="flex items-center gap-4 p-3 rounded-lg bg-white/5">
                              <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
                              <input
                                type="time"
                                value={slot.startTime}
                                onChange={(e) => updateSlot(globalIdx, "startTime", e.target.value)}
                                className="bg-background border border-white/10 rounded px-3 py-1.5 text-sm text-white"
                              />
                              <span className="text-muted-foreground">to</span>
                              <input
                                type="time"
                                value={slot.endTime}
                                onChange={(e) => updateSlot(globalIdx, "endTime", e.target.value)}
                                className="bg-background border border-white/10 rounded px-3 py-1.5 text-sm text-white"
                              />
                              <Button size="sm" variant="ghost" onClick={() => removeSlot(globalIdx)} className="text-red-400 hover:bg-red-500/10 ml-auto">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="glass-panel border-white/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-display text-white flex items-center gap-2">
              <CalendarOff className="w-5 h-5 text-red-400" /> Blocked Dates
            </CardTitle>
            <p className="text-sm text-muted-foreground">Block specific dates when you're unavailable (vacations, days off)</p>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-4">
              <input
                type="date"
                value={newBlockDate}
                onChange={(e) => setNewBlockDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="bg-background border border-white/10 rounded px-3 py-1.5 text-sm text-white"
              />
              <input
                type="text"
                value={newBlockReason}
                onChange={(e) => setNewBlockReason(e.target.value)}
                placeholder="Reason (optional)"
                className="bg-background border border-white/10 rounded px-3 py-1.5 text-sm text-white flex-1"
              />
              <Button size="sm" onClick={addBlockedDate} disabled={!newBlockDate} className="bg-red-600 hover:bg-red-700 text-white">
                <Plus className="w-4 h-4 mr-1" /> Block
              </Button>
            </div>
            {blockedDates.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No dates blocked</p>
            ) : (
              <div className="space-y-2">
                {blockedDates
                  .sort((a, b) => a.date.localeCompare(b.date))
                  .map((bd) => (
                    <div key={bd.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <div className="flex items-center gap-3">
                        <CalendarOff className="w-4 h-4 text-red-400" />
                        <span className="text-white font-medium">{new Date(bd.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span>
                        {bd.reason && <span className="text-muted-foreground text-sm">— {bd.reason}</span>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeBlockedDate(bd.id)} className="text-red-400 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
